import { useParams } from "react-router-dom";
import { useState } from "react";

import { T, useT } from "@repo/transifex";
import { useSessionOrders } from "~/hooks/queries/orders";
import { type Till } from "~/api/tills/types";
import { useTill } from "~/api/tills/hooks/use-till";
import {
  Button,
  DialogError,
  DialogLoading,
  HeaderSeparator,
  SettingsLayout,
  TillStatusButton
} from "~/components";
import { TransactionsList } from "~/components/Transactions/transactions-list";
import { OrdersSearchInput } from "~/components/Transactions/orders-search-input";

type LayoutProps = {
  children: React.ReactNode;
  subtitle?: string;
  status?: "open" | "closed";
  tillId?: Till["id"];
  action?: React.ReactNode;
};

const Layout = ({
  children,
  subtitle,
  status,
  tillId,
  action
}: LayoutProps) => {
  const t = useT();

  return (
    <SettingsLayout
      backUrl={`/settings/tills/${tillId}`}
      className="pb-0"
      headerChildren={
        <>
          {status ? <TillStatusButton status={status} /> : null}
          <HeaderSeparator />
          <p className="text-2xl font-medium text-text-primary">
            <T _str="Transactions" />
          </p>
        </>
      }
      subtitle={subtitle}
      title={t("Register Management")}
      action={action}
    >
      {children}
    </SettingsLayout>
  );
};

export const TillSessionTransactionPage = () => {
  const [searchString, setSearchString] = useState("");
  const { tillId, sessionId } = useParams();

  const { data: till, status: tillStatus } = useTill(tillId);
  const {
    data: orders,
    status: ordersStatus,
    refetch,
    isFetching
  } = useSessionOrders({ sessionId, searchString });

  if (tillStatus === "pending")
    return (
      <Layout>
        <DialogLoading />
      </Layout>
    );

  if (tillStatus === "error" || ordersStatus === "error") {
    return (
      <Layout>
        <DialogError>
          <Button onClick={() => refetch()} loading={isFetching}>
            <T _str="Retry" />
          </Button>
        </DialogError>
      </Layout>
    );
  }

  return (
    <Layout
      status={till.sessionId ? "open" : "closed"}
      subtitle={till.name}
      tillId={tillId}
      action={
        <div className="w-1/3">
          <OrdersSearchInput onSearch={setSearchString} />
        </div>
      }
    >
      <TransactionsList
        data={orders?.orders ?? []}
        isLoading={ordersStatus === "pending"}
      />
    </Layout>
  );
};
